import { element } from 'protractor';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import { TranslocoService } from '@ngneat/transloco';
import { CompetenceModel } from 'projects/core/src/lib/models/competence.model';

export interface AcCompetenceOverviewChartDataType {
  competence?: CompetenceModel;
  title?: string;
  elements: any;
}

export interface AcCompetenceClickedChartDataType {
  title?: string;
  color?: string;
  data: any;
  filters?: {
    competence: CompetenceModel | null;
    element: string | null;
  };
}

const COLORS = ['#b90060', '#ef6096', '#666666', '#777777', '#888888', '#999999', '#aaaaaa', '#bbbbbb', '#cccccc', '#dddddd'];

@Component({
  selector: 'app-ac-competence-overview-chart',
  templateUrl: './ac-competence-overview-chart.component.html',
  styleUrls: ['./ac-competence-overview-chart.component.scss'],
})
export class AcCompetenceOverviewChartComponent implements OnInit, OnDestroy, AfterViewInit {
  private chart?: am4charts.XYChart;
  private language: string;

  @Input() data: AcCompetenceOverviewChartDataType;
  @Output() barClicked = new EventEmitter<any>();
  @ViewChild('chartContainer') chartContainer?: ElementRef = null;

  constructor(
    private zone: NgZone,
    private transloco: TranslocoService
  ) {}

  ngOnInit(): void {
    this.language = this.transloco.getActiveLang().toUpperCase();
  }

  ngOnDestroy(): void {
    this.zone.runOutsideAngular(() => {
      this.chart?.dispose();
    });
  }

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      if (this.chartContainer === null) {
        return;
      }
      am4core.useTheme(am4themes_animated);

      const chart = am4core.create(this.chartContainer.nativeElement, am4charts.XYChart);
      chart.height = 300;
      // chart.legend = new am4charts.Legend();
      chart.data = this.getChartData();

      const title = chart.titles.create();
      title.text = (this.data.title) ? this.data.title : ((this.language) ? this.data.competence.name[this.language] : '');
      title.marginBottom = 10;

      const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
      categoryAxis.dataFields.category = 'component';
      categoryAxis.renderer.minGridDistance = 20;

      const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      valueAxis.min = 0;
      valueAxis.max = 5;
      valueAxis.renderer.minGridDistance = 30;

      const label = categoryAxis.renderer.labels.template;
      label.truncate = true;
      label.maxWidth = 120;

      const series = chart.series.push(new am4charts.ColumnSeries());
      series.dataFields.valueY = 'value';
      series.dataFields.categoryX = 'component';

      series.columns.template.adapter.add('fill', (fill, target) => {
        const data: any = target.dataItem.dataContext;
        return am4core.color(data.color);
      });

      series.columns.template.adapter.add('stroke', (stroke, target) => {
        const data: any = target.dataItem.dataContext;
        return am4core.color(data.color);
      });

      // tooltip
      const tooltipText
        = '{title}\n'
        + this.transloco.translate('common.labels.average') + ': {value}\n'
        + this.transloco.translate('common.labels.ratings') + ': {amount}';
      series.columns.template.tooltipText = tooltipText;

      const valueLabel = series.bullets.push(new am4charts.LabelBullet());
      valueLabel.label.text = '{value}';
      valueLabel.label.fontSize = 14;
      valueLabel.label.adapter.add('dy', (dy, target) => {
        if (!target.dataItem) {
          return dy;
        }
        return (target.dataItem.values.valueY.value < 1) ? -10 : 10;
      });
      valueLabel.label.adapter.add('fill', (fill, target) => {
        if (!target.dataItem) {
          return fill;
        }
        return (target.dataItem.values.valueY.value === 0) ? am4core.color('#000') : am4core.color('#fff');
      });

      series.columns.template.events.on(
        'hit',
        (ev: any) => {
          this.barClicked.emit(this.getDrilldownData(ev));
        },
        this
      );

      this.chart = chart;
    });
  }

  private getChartData(): any {
    const data: Array<any> = [];
    let overallSum = 0;
    let overallCount = 0;
    Object.keys(this.data.elements).forEach((elementKey: string, barCount: number) => {
      const values = this.data.elements[elementKey];
      let sum = 0;
      let count = 0;
      Object.keys(values).forEach((valueKey: string) => {
        const points = parseFloat(valueKey);
        sum += (values[valueKey] * points);
        count += values[valueKey];
        overallSum += (values[valueKey] * points);
        overallCount += values[valueKey];
      });
      if (elementKey !== 'exercises' || count > 0) {
        data.push({
          element: elementKey,
          component: (elementKey === 'exercises') ? this.transloco.translate('common.labels.exercises') : elementKey,
          title: (elementKey === 'exercises') ? this.transloco.translate('common.labels.exercises') : elementKey,
          value: (count > 0) ? Math.round(sum / count * 10) / 10 : 0,
          amount: count,
          color: (elementKey === 'exercises') ? COLORS[1] : COLORS[2]
        });
      }
    });
    data.sort((a: any, b: any) => {
      if (a.element === 'exercises') {
        return -1;
      } else if (b.element === 'exercises') {
        return 1;
      } else {
        return (a.title > b.title) ? 1 : ((b.title > a.title) ? -1 : 0);
      }
    });
    if (overallCount > 0) {
      data.unshift({
        element: 'overall',
        component: this.transloco.translate('common.labels.overall'),
        title: this.transloco.translate('common.labels.overall'),
        value: (overallCount > 0) ? Math.round(overallSum / overallCount * 10) / 10 : 0,
        amount: overallCount,
        color: COLORS[0]
      });
    }
    return data;
  }

  private getDrilldownData(ev: any): AcCompetenceClickedChartDataType {
    const competence = this.data.competence || null;
    const dataContext: any = ev.target.dataItem.dataContext;
    const title = (this.data.title) ? this.data.title : competence.name[this.language];
    const data: AcCompetenceClickedChartDataType = {
      title: title + ' - ' + dataContext.component,
      color: dataContext.color,
      data: this.getConsolidatedData(this.data.elements, dataContext.element),
      filters: {
        competence,
        element: dataContext.element || null
      }
    };
    return data;
  }

  private getConsolidatedData(data: any, type: string): any {
    const result = [];
    const temp: any = {};
    Object.keys(data).forEach((key: string) => {
      if (type === 'overall' || type === key) {
        Object.keys(data[key]).forEach((dataKey: string) => {
          if (data[key][dataKey] && data[key][dataKey] > 0) {
            if (!temp[dataKey]) {
              temp[dataKey] = 0;
            }
            temp[dataKey] += data[key][dataKey];
          }
        });
      }
    });
    Object.keys(temp).forEach((key: string) => {
      result.push({
        points: parseFloat(key),
        value: temp[key]
      });
    });
    return result;
  }

}
